import { Button, Stack, TextField, Typography } from '@mui/material'
import { twinsNewInsightCategories } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { TwinInsightCategory } from 'api/model'
import { COLORS } from 'app-constants'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useActiveOrganization } from 'hooks/use-organization'
import { useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import { LoadingButton } from '@mui/lab'

const AddTopicForm = ({
  topicInput,
  instructionsInput,
  setTopicInput,
  setInstructionsInput,
  isDuplicateTopicError,
  setIsDuplicateTopicError,
}: {
  topicInput: string
  instructionsInput: string
  setTopicInput: React.Dispatch<React.SetStateAction<string>>
  setInstructionsInput: React.Dispatch<React.SetStateAction<string>>
  isDuplicateTopicError: boolean
  setIsDuplicateTopicError: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const onTopicInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTopicInput(e.target.value)
    setIsDuplicateTopicError(false)
  }

  const onInstructionsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstructionsInput(e.target.value)
  }

  return (
    <Stack
      borderRadius="8px"
      p="12px"
      pt="32px"
      sx={{
        background: COLORS.GRAY_8,
      }}>
      <TextField
        className="has-label"
        required={true}
        value={topicInput}
        onChange={onTopicInputChange}
        label="Topic"
        size="small"
        helperText={isDuplicateTopicError ? 'This topic already exists' : ''}
        error={isDuplicateTopicError}
        placeholder="e.g. Finances"
        inputProps={{ sx: { pl: 0 } }}
        sx={{
          width: '100%',
          mb: '42px',
        }}
      />

      <TextField
        className="has-label"
        value={instructionsInput}
        onChange={onInstructionsInputChange}
        label="Additional instructions"
        size="small"
        placeholder="e.g. Show me profit or loss statements"
        inputProps={{ sx: { pl: 0 } }}
        sx={{
          width: '100%',
          mb: '42px',
        }}
      />
    </Stack>
  )
}

const AddTopicModal = ({
  onClose,
  twinId,
  insightCategories,
  onUpdateInsights,
}: {
  onClose: () => void
  twinId: string
  insightCategories: TwinInsightCategory[]
  onUpdateInsights: (updatedInsightCateories: TwinInsightCategory[]) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()

  const [updatedInsights, setUpdatedInsights] = useState<TwinInsightCategory[]>([])
  // on initial mount, we want to set the updated topics to the topics that are passed in
  useEffect(() => {
    setUpdatedInsights(insightCategories)
  }, [insightCategories])

  // state for add topic form
  const [topicInput, setTopicInput] = useState<string>('')
  const [instructionsInput, setInstructionsInput] = useState<string>('')

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [isDuplicateTopicError, setIsDuplicateTopicError] = useState<boolean>(false)

  const onSaveClick = async () => {
    if (!activeOrganization || !topicInput) {
      return
    }

    setIsSubmitting(true)

    const isDuplicate = updatedInsights.map((i) => i.name.toLowerCase()).includes(topicInput.toLowerCase())
    if (isDuplicate) {
      setIsDuplicateTopicError(true)
      return
    }

    const newTopic = instructionsInput ? { name: topicInput, instruction: instructionsInput } : { name: topicInput }

    try {
      // we want to capture a list of updated insights to use to store on client side
      let finalInsightCategories = [...updatedInsights]

      const response = await twinsNewInsightCategories(activeOrganization, twinId, [newTopic])
      finalInsightCategories.push(...response.data)

      setToast({ title: 'Successfully updated insights', severity: 'success' })

      // refetches insights after the user confirms
      // updates local client state with updated categories
      onUpdateInsights(finalInsightCategories)

      onClose()
    } catch (e) {
      setToast({ title: 'Failed to update insights', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      width={isMobile ? 'calc(100% - 32px)' : '960px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      <Typography variant="h4" color="secondary" mb={4}>
        Add topic of interest
      </Typography>

      <AddTopicForm
        topicInput={topicInput}
        instructionsInput={instructionsInput}
        setTopicInput={setTopicInput}
        setInstructionsInput={setInstructionsInput}
        isDuplicateTopicError={isDuplicateTopicError}
        setIsDuplicateTopicError={setIsDuplicateTopicError}
      />

      <ModalButtonsContainerStack>
        <Button onClick={onClose} size="medium" color="primary">
          Cancel
        </Button>
        <LoadingButton loading={isSubmitting} color="primary" variant="contained" size="medium" onClick={onSaveClick}>
          Save
        </LoadingButton>
      </ModalButtonsContainerStack>
    </JelledModal>
  )
}

export default AddTopicModal
